/**
 * The reads the Progress screen makes, over the client's query options and
 * T11's hooks:
 *
 * - `GET /me/progress` (T33, `@repo/client/data/progress`), in the catalogue
 *   locale, so muscle and exercise names arrive translated and are never
 *   translated again here;
 * - `GET /me/stats` (T19) for the streak;
 * - `GET /me/measurements?kind=weight` (T20) over the same window, for the
 *   weight's trend (`D218`: logged on the profile, charted here);
 * - `GET /me/retrospective` (T34) for whether the Retrospectiva entry has a
 *   closed week to open, and the three numbers its fan shows. See
 *   `useRetrospectiveEntry`.
 */
import { measurementsOptions, profileOptions } from "@repo/client/data/profile";
import {
  lifetimeOptions,
  progressOptions,
  recoveryOptions,
  retrospectiveOptions,
} from "@repo/client/data/progress";
import { trainingStatsOptions } from "@repo/client/data/workout";
import {
  PROGRESS_DEFAULT_WEEKS,
  type RetrospectiveOutput,
} from "@repo/contract/training";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../data/api";
import { useDataQuery } from "../../data/query-client";
import { coveringMeasurements } from "../../data/sessions-placeholder";
import { catalogueLocale } from "../../lib/locale";
import { useRetrospective } from "../wrapped/use-retrospective";
import { windowStart } from "./model";

/** The window the screen shows: A2's eight weeks, the server's default. */
export const PROGRESS_WEEKS = PROGRESS_DEFAULT_WEEKS;

export function useProgress() {
  const api = useApi();
  return useDataQuery(
    progressOptions(api, { weeks: PROGRESS_WEEKS, locale: catalogueLocale() })
  );
}

export function useTrainingStats() {
  const api = useApi();
  return useDataQuery(trainingStatsOptions(api));
}

/** `GET /me/recovery` (`D203`) in the catalogue locale — Progress and Today
 *  share the one key, so either screen opens on the other's read. */
export function useRecovery() {
  const api = useApi();
  return useDataQuery(recoveryOptions(api, { locale: catalogueLocale() }));
}

/** `GET /me/lifetime` (`D204`): the totals and the achievements. Read on
 *  Progress only — it reads every set, which Today must never pay for. */
export function useLifetime() {
  const api = useApi();
  return useDataQuery(lifetimeOptions(api));
}

/** The body the maps draw: the profile's sex, else the canonical male, as
 *  the exercise sheet draws it (`D199`). */
export function useBodySex(): "male" | "female" {
  const api = useApi();
  const profile = useDataQuery(profileOptions(api));
  return profile.data?.sex === "female" ? "female" : "male";
}

/**
 * Whether the last ended month's or year's story is ready (`D204`), for
 * Progress's entries. A failed read is "not ready": an entry never opens an
 * empty story.
 */
export function usePeriodRetrospective(period: "month" | "year"): {
  label: string | null;
  ready: boolean;
} {
  const api = useApi();
  const query = useDataQuery(
    retrospectiveOptions(api, { locale: catalogueLocale(), period })
  );
  const { data } = query;
  return {
    label: (period === "month" ? data?.month : data?.year) ?? null,
    ready: data?.ready === true,
  };
}

/** Body weight since the Monday that opens the progress window. */
export function useBodyWeight() {
  const api = useApi();
  const client = useQueryClient();
  const from = windowStart(new Date(), PROGRESS_WEEKS);
  // Keyed by the week the window opens: on a new week, draw the cached read
  // that covers it while the new one is on the wire (`D194`).
  return useDataQuery(measurementsOptions(api, { kind: "weight", from }), {
    placeholderData: () =>
      coveringMeasurements(client, { from, kind: "weight" }),
  });
}

/**
 * Whether last week's Retrospectiva is ready (contracts §7 `ready`), and the
 * cards the entry's fan already shows.
 *
 * It calls T65's own `useRetrospective`, rather than `retrospectiveOptions`
 * again: the catalogue locale is part of the key
 * (`progressKeys.retrospective`), so a call that omits it is a SECOND cache
 * entry and a second request for the same answer — which is what this file
 * used to do while claiming the opposite. One hook, one key, one request,
 * for the entry, the story and Search's `wrapped` action alike.
 *
 * Any failure reads as "not ready", so the entry never opens an empty story,
 * and `cards` is `null` until the server sends them — the fan then shows
 * dashes, not numbers nobody computed.
 */
export function useRetrospectiveEntry(): {
  cards: RetrospectiveOutput["cards"];
  ready: boolean;
} {
  const query = useRetrospective();
  return {
    cards: query.data?.cards ?? null,
    ready: query.data?.ready === true,
  };
}
